import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, ChevronRight } from "lucide-react";
import { formatCalendarDate, formatScheduledTime } from "../../lib/calendar-date";
import {
  isJobStatus,
  jobStatusLabel,
  jobStatusTint,
  jobStatusStyle,
  type JobStatus,
} from "../../lib/job-status";
import { LoadFailureNotice } from "../ui/LoadFailureNotice";
import type { LoadFailureKind } from "../../lib/load-failure";

interface JobItem {
  id: string;
  job_number?: string;
  jobNumber?: string;
  customer_name?: string;
  customerName?: string;
  customer?: { id: string; name: string } | null;
  service_type?: string;
  serviceType?: string;
  well_name?: string;
  wellName?: string;
  rig_name?: string;
  rigName?: string;
  status: string;
  scheduled_date?: string;
  scheduledDate?: string;
}

interface JobStatusBoardProps {
  jobs: JobItem[];
  /**
   * Set when the jobs read never arrived. "No active jobs" is a fact about the
   * schedule, and a failed read knows nothing about the schedule.
   */
  failure?: LoadFailureKind | null;
}

/**
 * `completed` IS NO LONGER RED — on any of the things this board colours.
 *
 * There were three local overrides here, not one, and they stood or fell
 * together: the card tint, the dot, and the left stripe. All three existed for
 * one reason, spelled out in job-status.ts — the canonical `bg-emerald-500` dot
 * measured 2.54:1 on a light card, worse than the `bg-red-500` (3.76) this
 * board and JobsScreen were already using, so `completed` could not converge
 * anywhere. The solid fills remap per theme and emerald measures 4.20-9.17
 * across every surface, so the reason is gone and so are the overrides.
 * Deleting only the dot would have been worse than keeping all three: an
 * emerald dot on a red card under a red stripe.
 *
 * The tint is the canonical `JOB_STATUS_TINT` unmodified, so there is no
 * board-local fill map left to drift, and `text-muted-foreground` on it is
 * unchanged.
 *
 * WHAT THIS BOARD ORIGINALLY GOT WRONG, still worth knowing: the map before
 * these was keyed on the semantic tokens rather than the status hues, and it
 * collapsed two pairs onto identical values — `scheduled` and `on_hold` both
 * `bg-warning`, `completed` and `cancelled` both `bg-muted`. Each pair
 * composited to the same bytes, rgb(238,228,211) and rgb(228,232,236) in light
 * mode, so two of the six statuses were distinguishable only by reading their
 * label.
 */

/**
 * The card's left edge, by status: the five signal fills as `border-l-*`
 * classes. This was a hex per status in an inline style plus a 30% border
 * tint; the edge now reads the same tokens as every other status rail, and the
 * 4px width matches the register's rail. On hold shares amber with Scheduled
 * (both are "not moving yet"); completed and cancelled share slate. The pill
 * carries the name.
 */
const BOARD_EDGE: Record<JobStatus, string> = {
  pending: "border-l-fi-signal-blue",
  scheduled: "border-l-fi-signal-amber",
  active: "border-l-fi-signal-green",
  on_hold: "border-l-fi-signal-amber",
  completed: "border-l-fi-signal-slate",
  cancelled: "border-l-fi-signal-slate",
};
const NEUTRAL_EDGE = "border-l-transparent";

/**
 * True while the strip has cards off its right edge. Measured on scroll and on
 * resize; jsdom has no layout, so under test it is always false.
 */
function useMoreToRight(ref: React.RefObject<HTMLDivElement | null>, deps: unknown[]) {
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return more;
}

const JobStatusBoard = ({ jobs, failure }: JobStatusBoardProps) => {
  const navigate = useNavigate();
  const stripRef = useRef<HTMLDivElement>(null);
  const moreToRight = useMoreToRight(stripRef, [jobs.length, failure]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.35, ease: "easeOut" }}
    >
      {failure ? (
        <LoadFailureNotice error={null} kind={failure} subject="jobs" className="py-6" />
      ) : jobs.length === 0 ? (
        <p className="py-6 text-center text-sm text-fieldiq-muted">No active jobs</p>
      ) : (
        <div className="relative">
        <div
          ref={stripRef}
          data-testid="job-board-strip"
          className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-hide"
        >
          {jobs.map((job, i) => {
            // Unknown/absent statuses fall through to the canonical helpers, which
            // land on a neutral fill and echo the raw value. The map this
            // replaced defaulted to `statusConfig.pending`, so a status the
            // frontend had never seen rendered as a real, actionable "Pending".
            const known = isJobStatus(job.status);
            const status = job.status as JobStatus;
            const cfg = {
              bg: jobStatusTint(job.status),
              pill: jobStatusStyle(job.status),
              label: jobStatusLabel(job.status),
              edge: known ? BOARD_EDGE[status] : NEUTRAL_EDGE,
            };
            /**
             * `scheduled_date` is read as a CALENDAR DAY — the same treatment
             * DispatchScreen.tsx:266 gives it, so the board and the dashboard
             * cannot disagree about which day a job is on.
             *
             * The column is MIXED in the database: some rows sit at UTC
             * midnight, most on exact hours. Taking the date-part prefix is
             * right for both TODAY, because both come from NewJobScreen writing
             * the user's picked day into that prefix — either bare
             * 'YYYY-MM-DD', or 'YYYY-MM-DDTHH:mm' as offset-less local wall
             * clock. `new Date()` on the second kind shifted an early-morning
             * job to the previous day.
             *
             * THAT OPEN QUESTION IS NOW ANSWERED, and the answer is that the
             * prefix read is correct. Verified 2026-09-09: production's session
             * zone is Etc/UTC, so the offset-less wall clock NewJobScreen sends
             * is stored verbatim and the date part IS the operator's day. It
             * stays a prefix read, not an instant read.
             *
             * The half that was wrong was "most on exact hours" being treated
             * as noise to discard: 125 of 150 scheduled rows carry a real time
             * of day, and no screen rendered it. formatScheduledTime does, and
             * reads the same literal UTC clock for the same reason.
             *
             * REVISIT IF THE WRITE SIDE CHANGES: if `scheduled_date` ever
             * becomes a true timestamptz with real offsets, both reads have to
             * become instant reads together.
             */
            const sd = job.scheduled_date || job.scheduledDate || '';
            const dateStr = formatCalendarDate(sd, { month: 'short', day: 'numeric' }) ?? '';
            const timeStr = formatScheduledTime(sd);
            const location = job.well_name || job.wellName || job.rig_name || job.rigName || '';

            return (
              <motion.button
                key={job.id}
                onClick={() => navigate(`/jobs/${job.id}`)}
                className={`min-w-[260px] max-w-[320px] snap-start ${cfg.bg} border border-fieldiq-border border-l-4 ${cfg.edge} rounded-fi-panel p-4 text-left transition-all duration-200 active:scale-[0.98] flex-shrink-0`}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.4, ease: "easeOut" }}
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {/* THE CARD STAYS TINTED AND THE LABEL BECOMES THE PILL. The card
                        is not the status — it is a container for a job that has that
                        status — and six 320px cards at full saturation is a wall of
                        colour rather than a board. The tint holds the group, the solid
                        pill carries the signal, and it carries it harder than the old
                        dot-plus-label did: `scheduled` and `on_hold` separate at dE 18.9
                        in dark and 6.9 in light as solids, against 9.5 and 5.3 as the
                        tint the dot sat on.

                        THE DOT IS GONE. It was `bg-status-<hue>` immediately beside a
                        label that is now a solid pill of that same hue — punctuation
                        repeating what the thing next to it already says at full
                        strength. PR #396 found the dot load-bearing for telling
                        `scheduled` from `on_hold` here; the pill took that job over with
                        roughly double the separation, which is the only reason dropping
                        it is safe.

                        `text-foreground` went with it: the label is `text-status-fg` now,
                        inside the pill, measuring 5.02-10.56. The secondary text below
                        keeps `text-muted-foreground` on the tint, unchanged. */}
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.pill}`}>{cfg.label}</span>
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {job.job_number || job.jobNumber || ''}
                  </span>
                </div>
                <p className="text-sm font-bold text-foreground mb-1 truncate">
                  {job.customer_name || job.customerName || job.customer?.name || 'No Client'}
                </p>
                <p className="text-xs text-muted-foreground mb-2 truncate">
                  {job.service_type || job.serviceType || ''}
                </p>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-2 border-t border-white/5">
                  {location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {location.split(",")[0]}
                    </span>
                  )}
                  {dateStr && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      {timeStr ? `${dateStr} ${timeStr}` : dateStr}
                    </span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
        {/* THE CUE THAT THERE IS MORE. At 375px one card fills the strip and
            the next starts off screen, and a hidden scrollbar leaves nothing to
            say so. A fade and a label at the right edge while cards remain. */}
        {moreToRight && (
          <div
            data-testid="job-board-more"
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 flex w-16 items-center justify-end bg-gradient-to-l from-fieldiq-card to-transparent pb-2"
          >
            <span className="flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-amber px-1.5 py-0.5 text-fi-signal-ink">
              <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        )}
        </div>
      )}
      {jobs.length > 1 && !failure && (
        <p className="mt-1 text-[11px] text-fieldiq-muted md:hidden">Swipe for more jobs</p>
      )}
    </motion.div>
  );
};

export default JobStatusBoard;
